import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { complianceService } from '../../../services/adminService';

interface Props {
  programId: string;
}

interface ConsentPayload {
  purposes: Record<string, boolean>;
  version?: string;
}

const ConsentSettings: React.FC<Props> = ({ programId }) => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [data, setData] = useState<ConsentPayload>({ purposes: {}, version: 'v1' });

  useEffect(() => {
    const run = async () => {
      if (!programId) return;
      setLoading(true);
      try {
        const res = await complianceService.getConsentSettings(programId);
        setData({ purposes: res?.purposes ?? {}, version: res?.version ?? 'v1' });
      } catch (e: any) {
        toast.error(e?.message || 'Failed to load consent settings');
      } finally {
        setLoading(false);
      }
    };
    run();
  }, [programId]);

  const togglePurpose = (key: string) => {
    setData((prev) => ({ ...prev, purposes: { ...prev.purposes, [key]: !prev.purposes[key] } }));
  };

  const addPurpose = () => {
    const key = prompt('Enter new purpose key (e.g., analytics):');
    if (!key) return;
    setData((prev) => ({ ...prev, purposes: { ...prev.purposes, [key]: true } }));
  };

  const save = async () => {
    if (!programId) {
      toast.error('Program ID required');
      return;
    }
    setSaving(true);
    try {
      await complianceService.updateConsentSettings(programId, data);
      toast.success('Consent settings saved');
    } catch (e: any) {
      toast.error(e?.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-medium">Consent Settings</h2>
        <div className="flex gap-2">
          <button onClick={addPurpose} className="px-3 py-2 text-sm rounded border">Add Purpose</button>
          <button onClick={save} disabled={saving || !programId} className="px-3 py-2 text-sm rounded bg-indigo-600 text-white disabled:opacity-50">
            {saving ? 'Saving...' : 'Save'}
          </button>
        </div>
      </div>
      {!programId && <div className="text-sm text-gray-500">Enter a Program ID to load settings.</div>}
      {loading ? (
        <div>Loading...</div>
      ) : (
        <div className="border rounded divide-y">
          {Object.keys(data.purposes).length === 0 && (
            <div className="p-4 text-sm text-gray-500">No purposes configured. Click "Add Purpose" to create one.</div>
          )}
          {Object.entries(data.purposes).map(([key, val]) => (
            <div key={key} className="flex items-center justify-between p-4">
              <div>
                <div className="font-medium">{key}</div>
                <div className="text-xs text-gray-500">Purpose key</div>
              </div>
              <label className="inline-flex items-center cursor-pointer">
                <input type="checkbox" className="sr-only" checked={!!val} onChange={() => togglePurpose(key)} />
                <div className={`w-10 h-5 rounded-full transition ${val ? 'bg-green-500' : 'bg-gray-300'}`}></div>
              </label>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ConsentSettings;
